import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, BookOpen, Home, Clock, ArrowRight, Heart, Sparkles, Library, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_volunteer-mgmt-sys/artifacts/regm3bui_Gemini_Generated_Image_wj21ojwj21ojwj21.png";
const VOLUNTEER_IMAGE = "https://customer-assets.emergentagent.com/job_volunteer-mgmt-sys/artifacts/r6yruvh9_Gemini_Generated_Image_87qfmd87qfmd87qf.png";
const CORPORATE_IMAGE = "https://customer-assets.emergentagent.com/job_82aee9b5-a3d4-4458-b4bf-4495602ae167/artifacts/ualwbzue_Gemini_Generated_Image_neneb9neneb9nene.png";

export default function LandingPage() {
  const [stats, setStats] = useState({
    volunteers: 0,
    bookshelves: 0,
    families: 0,
    hours: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/public/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Use default values
        setStats({ volunteers: 150, bookshelves: 120, families: 350, hours: 2500 });
      }
    };
    fetchStats();
  }, []);

  const impactStats = [
    { icon: BookOpen, label: 'Bookshelves Distributed', value: 250, color: 'text-[#1a3a5c]', bgColor: 'bg-[#1a3a5c]/10' },
    { icon: Home, label: 'Children Served', value: '500+', color: 'text-[#5cc8e8]', bgColor: 'bg-[#5cc8e8]/10' },
    { icon: Library, label: 'Books Distributed', value: '5,000+', color: 'text-[#F4D03F]', bgColor: 'bg-[#F4D03F]/20' },
  ];

  const volunteerRoles = [
    { title: 'Bookshelf Assembler', description: 'Assemble bookshelves following instructions. Inspect for stability and safety, report any defects or issues.' },
    { title: 'Bookmobile Navigator', description: 'Safely transport bookshelves to families. Verify schedules, load/unload securely, and obtain delivery confirmations.' },
    { title: 'Bookshelf Artisan', description: 'Personalize bookshelves with paint and creative designs to make them special for each child.' },
    { title: 'Book Curator', description: 'Sort and organize donated books by age and reading level. Curate book bundles for families.' },
    { title: 'Literacy Ambassador', description: 'Represent the organization at community events, share program information, and engage with the public.' },
    { title: 'Event & Administrative Support', description: 'Assist with literacy events, fundraisers, data entry, social media, and organizational operations.' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="https://www.thebookshelfproject.org" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
            </a>
            <div className="flex items-center gap-4">
              <Link to="/donate">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]" data-testid="donate-link">
                  <Gift className="w-4 h-4 mr-2" />
                  Donate Books & Bookshelves
                </Button>
              </Link>
              <Link to="/corporate">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]" data-testid="corporate-link">
                  Corporate Volunteers
                </Button>
              </Link>
              <Link to="/apply">
                <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-6" data-testid="apply-nav-btn">
                  Volunteer Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#5cc8e8]/10 text-[#1a3a5c] px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Creating In-Home Libraries for Children
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1a3a5c] leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Every Child Deserves Books in Their Home
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                The Bookshelf Project builds in-home libraries for children across the Washington, DC region. 
                Our volunteers help ensure that every child has access to books and the joy of reading.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/apply">
                  <Button size="lg" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all" data-testid="hero-apply-btn">
                    Start Volunteer Application
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/volunteer-portal">
                  <Button size="lg" variant="outline" className="border-2 border-[#5cc8e8] text-[#1a3a5c] hover:bg-[#5cc8e8] hover:text-white rounded-full px-8 py-6 text-lg transition-all" data-testid="volunteer-portal-btn">
                    Volunteer Portal
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#5cc8e8]/20 rounded-3xl blur-2xl"></div>
              <img 
                src={HERO_IMAGE} 
                alt="Children reading together" 
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Yearly Impact Goals
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Your partnership can help us reach our yearly impact goals and make a real difference in children's lives across the DC region.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactStats.map((stat, index) => (
              <Card key={index} className="card-hover border-0 shadow-md" data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 ${stat.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <stat.icon className={`w-7 h-7 ${stat.color}`} />
                  </div>
                  <p className="text-3xl font-bold text-[#1a3a5c] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                  </p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Roles */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={VOLUNTEER_IMAGE} 
                alt="Volunteer working" 
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ways to Get Involved
              </h2>
              <p className="text-slate-600">
                We have volunteer opportunities for everyone, regardless of your skills or availability.
              </p>
              <div className="grid gap-4">
                {volunteerRoles.slice(0, 4).map((role, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="w-10 h-10 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Heart className="w-5 h-5 text-[#5cc8e8]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a3a5c]">{role.title}</h3>
                      <p className="text-sm text-slate-500">{role.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/volunteer-roles">
                <Button className="bg-[#5cc8e8] hover:bg-[#5cc8e8]/90 text-[#1a3a5c] rounded-full px-8 py-3 font-semibold shadow-md hover:shadow-lg transition-all" data-testid="roles-apply-btn">
                  View All Roles & Apply
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Volunteer Section */}
      <section className="py-20 bg-gradient-to-br from-[#5cc8e8]/10 to-[#1a3a5c]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#1a3a5c]/10 px-4 py-2 rounded-full text-sm font-medium text-[#1a3a5c] mb-4">
                <Users className="w-4 h-4" />
                Corporate Partners
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1a3a5c] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Bring Your Team for a Day of Impact
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed">
                In just one service day, your team can build enough bookshelves to serve 40+ families. 
                Corporate volunteer days strengthen team bonds while making a lasting difference in children's literacy.
              </p>
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-[#5cc8e8]">25</p>
                  <p className="text-xs text-slate-500">Volunteers</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-[#F4D03F]">40</p>
                  <p className="text-xs text-slate-500">Bookshelves</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl shadow-sm">
                  <p className="text-2xl font-bold text-emerald-500">40</p>
                  <p className="text-xs text-slate-500">Families</p>
                </div>
              </div>
              <Link to="/corporate">
                <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-8 py-3 font-semibold shadow-md hover:shadow-lg transition-all" data-testid="corporate-cta-btn">
                  Plan a Corporate Volunteer Day
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#5cc8e8]/20 rounded-3xl blur-xl"></div>
              <img 
                src={CORPORATE_IMAGE}
                alt="Corporate volunteers building bookshelves"
                className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#1a3a5c] to-[#2a4a6c] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Make a Difference?
          </h2>
          <p className="text-[#5cc8e8] text-lg mb-8 max-w-2xl mx-auto">
            Join our community of volunteers and help us build in-home libraries for children who need them most.
          </p>
          <Link to="/apply">
            <Button size="lg" className="bg-white text-[#1a3a5c] hover:bg-slate-100 rounded-full px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all" data-testid="cta-apply-btn">
              Start Your Application
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-16 w-auto mb-4" />
              <p className="text-slate-300 text-sm">
                Building in-home libraries for children.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li><Link to="/apply" className="hover:text-[#5cc8e8] transition-colors">Volunteer Application</Link></li>
                <li><Link to="/corporate" className="hover:text-[#5cc8e8] transition-colors">Corporate Volunteers</Link></li>
                <li><Link to="/donate" className="hover:text-[#5cc8e8] transition-colors">Donate Books & Bookshelves</Link></li>
                <li><a href="/admin/login" className="hover:text-[#5cc8e8] transition-colors">Admin Portal</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-300 text-sm">
                info@thebookshelfproject.org
              </p>
              <a href="https://www.thebookshelfproject.org" target="_blank" rel="noopener noreferrer" className="text-slate-300 text-sm hover:text-[#5cc8e8] transition-colors">
                www.thebookshelfproject.org
              </a>
            </div>
          </div>
          <div className="border-t border-slate-600 mt-8 pt-8 text-center text-slate-400 text-sm">
            © {new Date().getFullYear()} The Bookshelf Project. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
